import { createRouter, createWebHistory } from 'vue-router'
import { nextTick } from 'vue'
import { MetaService } from '../services/MetaService'
import { APP_ORIGIN, MARKETING_ORIGIN, isOnAppHost, isOnMarketingHost } from '../services/origins'
import { consumeProfileHandoff } from '../services/profileHandoff'
import { saveFreeProfile, saveEnterpriseProfile } from '../services/apiClient.js'
import { restoreSessionFromCookie, isEnterpriseAccountType } from '../services/session.js'

// Every route below is tagged marketingOnly or appOnly — see the router.beforeEach guard
// at the bottom of this file for what that tag actually does. A route with neither tag
// (there are none currently) would render identically on both domains, which is never
// actually correct for this app — every real page belongs to exactly one side.
const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/LandingView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Scribe11 | Software Continuity Intelligence',
      description: 'Know which of your applications will break, why, and what to fix first — before end-of-life, vendor risk, or a broken dependency becomes an outage.',
      canonical: 'https://www.scribe11.com/'
    }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/PricingView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Pricing | Scribe11',
      description: 'Transparent CAD pricing for Scribe11. No USD conversion. 14-day free trial on all paid plans.',
      canonical: 'https://www.scribe11.com/pricing'
    }
  },
  {
    path: '/security',
    name: 'Security',
    component: () => import('../views/SecurityView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Security & Privacy | Scribe11',
      description: 'How Scribe11 protects your data — TLS, session security, HMAC-signed reports, SSRF protections, and responsible disclosure.',
      canonical: 'https://www.scribe11.com/security'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/ContactView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Contact & Demo | Scribe11',
      description: 'Book a Scribe11 demo, ask about pricing, or discuss your software continuity program with our team.',
      canonical: 'https://www.scribe11.com/contact'
    }
  },
  {
    // Stage 2 (docs/roadmap.md §5, §11a): the primary workspace destination — Device/System
    // Passports and the Business Services they support, built from what you run, not from an
    // uploaded scan (docs/strategy.md: "not a source-code scanner with a passport attached").
    // Auth-gated client-side in the view itself (redirects to '/' with ?auth=1&next=/app/systems
    // if no profile is in localStorage) — that '/' target is itself marketingOnly, so on a
    // real split deployment the guard below turns it into a real cross-domain redirect to
    // www.scribe11.com automatically; nothing in the view itself needs to know about domains.
    path: '/app/systems',
    name: 'SystemsDevices',
    component: () => import('../views/SystemsDevicesView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Systems & Devices | Scribe11',
      description: 'Device Passports and the Business Services they support.',
      canonical: 'https://app.scribe11.com/app/systems'
    }
  },
  {
    // Source-code scanning: one optional evidence-intake path for systems that have source
    // code, not the product's front door (see /app/systems above). Same client-side auth gate
    // pattern, redirecting to '/' with ?auth=1&next=/app.
    path: '/app',
    name: 'Workspace',
    component: () => import('../views/WorkspaceView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Source Scans | Scribe11',
      description: 'Scan an application\'s source for a Continuity Risk score, findings, and an SBOM.',
      canonical: 'https://app.scribe11.com/app'
    }
  },
  {
    // Stage 4 (docs/roadmap.md §13, Watch/§3): monitoring run trigger + history. Same
    // client-side auth gate pattern as /app above.
    path: '/app/monitoring',
    name: 'Monitoring',
    component: () => import('../views/MonitoringView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Monitoring | Scribe11',
      description: 'Evidence staleness and exception-expiry monitoring runs.',
      canonical: 'https://app.scribe11.com/app/monitoring'
    }
  },
  {
    // Phase 9 (2026-09-22 restructuring, docs/roadmap.md §11a): portfolio-wide dashboard —
    // business exposure and decisions requiring attention. Same client-side auth gate
    // pattern as every other /app/* route.
    path: '/app/overview',
    name: 'Overview',
    component: () => import('../views/OverviewView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Overview | Scribe11',
      description: 'Business exposure and decisions requiring attention, across your whole portfolio.',
      canonical: 'https://app.scribe11.com/app/overview'
    }
  },
  {
    // Phase 9: every open finding across the portfolio, ranked by severity, with its
    // remediation proposal status attached.
    path: '/app/risks',
    name: 'RisksActions',
    component: () => import('../views/RisksActionsView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Risks & Actions | Scribe11',
      description: 'Every open finding across your portfolio, ranked by severity.',
      canonical: 'https://app.scribe11.com/app/risks'
    }
  },
  {
    // Phase 9: portfolio-wide recovery documentation/exercise summary — read-only; the
    // actual runbook/exercise CRUD stays on RecoveryPanel inside each subject's own page.
    path: '/app/recovery',
    name: 'Recovery',
    component: () => import('../views/RecoveryView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Recovery | Scribe11',
      description: 'Every documented or tested recovery across your portfolio.',
      canonical: 'https://app.scribe11.com/app/recovery'
    }
  },
  {
    // Phase 9: portfolio-wide migration plan summary — read-only; the actual plan/decision
    // CRUD stays on MigrationPanel inside each subject's own page.
    path: '/app/migration',
    name: 'MigrationPlans',
    component: () => import('../views/MigrationPlansView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Migration Plans | Scribe11',
      description: 'Every migration/disposition assessment across your portfolio.',
      canonical: 'https://app.scribe11.com/app/migration'
    }
  },
  {
    // Enterprise workspace — requires business/developer/enterprise account. Lives under
    // the app domain like every other authenticated destination, including its own
    // sign-in form (unauthenticated visitors still see it at app.scribe11.com/enterprise,
    // not bounced to marketing first — Enterprise has always had its own dedicated auth UI).
    path: '/enterprise',
    name: 'Enterprise',
    component: () => import('../views/EnterpriseView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Enterprise Workspace | Scribe11',
      description: 'Team continuity workspace with application inventory, risk scoring, remediation tracking, and SAML SSO.',
      canonical: 'https://app.scribe11.com/enterprise'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/PrivacyView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Privacy Policy | Scribe11',
      description: 'Scribe11 privacy policy — how we collect, use, and protect your data under PIPEDA.',
      canonical: 'https://www.scribe11.com/privacy'
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/TermsView.vue'),
    meta: {
      marketingOnly: true,
      title: 'Terms of Service | Scribe11',
      description: 'Scribe11 terms of service — acceptable use, billing, liability, and governing law.',
      canonical: 'https://www.scribe11.com/terms'
    }
  },
  {
    path: '/app/reports',
    name: 'Reports',
    component: () => import('../views/ReportsView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Reports | Scribe11',
      description: 'Signed passport exports and their verification.',
      canonical: 'https://app.scribe11.com/app/reports'
    }
  },
  {
    path: '/app/account',
    name: 'Account',
    component: () => import('../views/AccountView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      title: 'Account | Scribe11',
      description: 'Email verification, signed-in sessions, and workspace deletion.',
      canonical: 'https://app.scribe11.com/app/account'
    }
  },
  {
    // Targets of the two links the backend emails (server.go's passwordResetURL /
    // emailVerifyURL). Reachable signed out: the token in the query is the credential.
    path: '/app/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/AccountLinkView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      accountAction: 'reset',
      title: 'Reset password | Scribe11',
      description: 'Set a new password for your Scribe11 account.',
      canonical: 'https://app.scribe11.com/app/reset-password'
    }
  },
  {
    path: '/app/verify-email',
    name: 'VerifyEmail',
    component: () => import('../views/AccountLinkView.vue'),
    meta: {
      appOnly: true,
      noindex: true,
      accountAction: 'verify',
      title: 'Confirm email | Scribe11',
      description: 'Confirm the email address on your Scribe11 account.',
      canonical: 'https://app.scribe11.com/app/verify-email'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// The domain-split guard. On a real production deployment (APP_ORIGIN !== MARKETING_ORIGIN,
// enforced inside isOnAppHost/isOnMarketingHost), this makes EVERY navigation — a direct
// URL load, a plain <a href> full-page reload, or a programmatic router.push/replace/
// <router-link> from anywhere in the app — land on the correct domain, without any
// individual view needing to know which domain it's currently on. That's deliberate: every
// existing relative next=/app/systems-style redirect, every <router-link to="/app/...">,
// and every workspaceHref computed property throughout this codebase already works
// unmodified — this guard is the ONLY place cross-domain awareness lives.
// Account links emailed before 2026-09-23 used /app?reset=<token> and /app?verify=<token>;
// those tokens stay valid (1 h and 48 h), so map them onto the current pages.
function legacyAccountLink(to) {
  if (to.path !== '/app') return null
  if (to.query.reset) return { path: '/app/reset-password', query: { token: String(to.query.reset) }, replace: true }
  if (to.query.verify) return { path: '/app/verify-email', query: { token: String(to.query.verify) }, replace: true }
  return null
}

// Every SSO flow ends with the backend setting the session cookie and redirecting here with
// ?sso=<provider> (Google/Apple → /app, OIDC/SAML → /enterprise). The views gate on a
// localStorage profile, so fetch it from /auth/me before any of them mounts.
async function completeSSOLanding(to) {
  const profile = await restoreSessionFromCookie()
  if (!profile) return { path: '/', query: { auth: '1', error: 'sso_session' }, replace: true }
  const { sso, ...rest } = to.query
  const path = isEnterpriseAccountType(profile.accountType) ? '/enterprise' : (to.path === '/app' ? '/app/systems' : to.path)
  return { path, query: rest, replace: true }
}

router.beforeEach(async (to) => {
  // Consume a cross-domain profile handoff (see services/profileHandoff.js) before
  // anything else — the destination view's own onMounted auth-gate check runs
  // synchronously against localStorage, so the handoff has to be written and this
  // navigation redirected to the clean URL BEFORE that view ever mounts, not after.
  if (to.query._p) {
    const cleanPath = consumeProfileHandoff(to.query, to.fullPath, saveFreeProfile, saveEnterpriseProfile)
    if (cleanPath) {
      router.replace(cleanPath)
      return false
    }
  }
  const accountLink = legacyAccountLink(to)
  if (accountLink) return accountLink
  if (to.query.sso && (to.path === '/app' || to.path === '/enterprise')) {
    return completeSSOLanding(to)
  }
  if (isOnAppHost()) {
    // Bare app.scribe11.com/ with no query string is someone going straight to their
    // dashboard (a bookmark, typing the domain) — send them into it rather than bouncing
    // to marketing, which is what the generic marketingOnly rule below would otherwise do.
    // A query string present (e.g. ?auth=1&next=... from an auth-gate redirect) means this
    // navigation has real intent behind it, so it falls through to the normal rule instead.
    if (to.path === '/' && Object.keys(to.query).length === 0) {
      window.location.href = APP_ORIGIN + '/app/overview'
      return false
    }
    if (to.meta.marketingOnly) {
      window.location.href = MARKETING_ORIGIN + to.fullPath
      return false
    }
  } else if (isOnMarketingHost() && to.meta.appOnly) {
    window.location.href = APP_ORIGIN + to.fullPath
    return false
  }
  return true
})

// Keep keyboard and screen-reader users oriented after a route change. The target is
// deliberately the page heading, so query-string changes inside a view do not steal focus
// from a field or table control.
router.afterEach(async (to, from) => {
  // Same path, different query (a tab or a selected passport inside Systems & Devices, a
  // filter): the view handles its own focus. Without this check the comment above was not
  // actually true — every query change moved focus to the h1, which broke the ARIA tabs'
  // arrow-key focus movement and threw keyboard users out of the control they were using.
  if (from.matched.length && to.path === from.path) return
  await nextTick()
  const heading = document.querySelector('main h1, main [role="heading"]')
  if (heading instanceof HTMLElement) {
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
    heading.focus({ preventScroll: false })
  }
  if (typeof window.announceToScreenReader === 'function') {
    window.announceToScreenReader(to.meta.title || 'Page loaded')
  }
})

router.afterEach((to) => {
  MetaService.updateMeta(to.meta)
  MetaService.injectJsonLd()
})

export default router
